import { useState, type FormEvent } from 'react'
import {
  catalogNameKey,
  inBasket,
  type CatalogItem,
  type Store,
  type Template,
  type TripItemInputData,
  type TripWithItems,
} from '@sp/shared'
import { handleListPaste } from '../../lib/paste.ts'
import { formatQty, searchKey, splitEntries, type ParsedEntry } from '../../lib/format.ts'
import { newId } from '../../lib/storage.ts'
import { Icon } from '../../components/Icon.tsx'
import { Button, Card, Chip, TextField } from '../../components/ui.tsx'
import { ScrollRow } from '../../components/ScrollRow.tsx'

/** Kolik znameho zbozi se pri psani nabidne. */
const MAX_SUGGESTIONS = 5

export type NewTripItem = TripItemInputData & { id: string }

/**
 * Jedno pole pro pridavani do kazdeho nakupu (navrh v3, dnesni pole bezneho
 * nakupu): napovida zname zbozi ze sablon, rozumi „2 kg rýže" a carkam,
 * pod nim „Ze šablony" a casto kupovane veci. Formular se sesti poli zmizel.
 */
export function AddBar({
  trip,
  route,
  catalog,
  templates,
  frequent,
  primary,
  onAdd,
  onPickTemplate,
  onBackToPlanning,
}: {
  trip: TripWithItems
  /** Obchody na trase v poradi. */
  route: readonly Store[]
  catalog: readonly CatalogItem[]
  templates: readonly Template[]
  frequent: readonly string[]
  /** Pridavani je hlavni akce obrazovky (nakupovani) — zlate tlacitko. */
  primary: boolean
  /**
   * Nove polozky; unplaced = polozky, kterym obchod na trase vybrala appka
   * (nakup s vic obchody se pak zepta „Kam?").
   */
  onAdd(items: NewTripItem[], unplaced: string[]): void
  onPickTemplate(): void
  /** Pri nakupovani: zpet do planovani (mnozstvi, presouvani). */
  onBackToPlanning?: (() => void) | undefined
}) {
  const [text, setText] = useState('')
  const onTrip = new Set(trip.items.filter((i) => !inBasket(i)).map((i) => i.catalogItemId ?? `jmeno:${catalogNameKey(i.name)}`))
  const known = catalog.filter((ci) => !ci.archived)
  const byKey = new Map(known.map((ci) => [catalogNameKey(ci.name), ci]))

  function placeFor(ci: CatalogItem | null): { storeId: string | null; guessed: boolean } {
    if (route.length === 0) return { storeId: null, guessed: false }
    if (ci?.storeId && route.some((s) => s.id === ci.storeId)) return { storeId: ci.storeId, guessed: false }
    return { storeId: route[0]?.id ?? null, guessed: route.length > 1 }
  }

  function build(ci: CatalogItem | null, entry: ParsedEntry | null): { item: NewTripItem; guessed: boolean } {
    const { storeId, guessed } = placeFor(ci)
    const store = route.find((s) => s.id === storeId) ?? null
    const currency = store?.currency ?? ci?.typicalCurrency ?? null
    const explicitQty = entry !== null && (entry.qty !== 1 || entry.unit !== null)
    return {
      guessed,
      item: {
        id: newId(),
        name: ci?.name ?? entry?.name ?? '',
        catalogItemId: ci?.id ?? null,
        storeId,
        qty: explicitQty ? (entry?.qty ?? 1) : (ci?.defaultQty ?? 1),
        unit: explicitQty ? (entry?.unit ?? null) : (ci?.unit ?? null),
        // Cena ze sablony jen ve stejne mene jako obchod na nakupu.
        expectedPriceMinor: ci && ci.typicalCurrency === currency ? ci.typicalPriceMinor : null,
        expectedCurrency: currency,
        note: ci?.note ?? null,
        source: ci ? 'template' : 'manual',
      },
    }
  }

  function add(parts: { item: NewTripItem; guessed: boolean }[]) {
    if (parts.length === 0) return
    onAdd(
      parts.map((p) => p.item),
      parts.filter((p) => p.guessed).map((p) => p.item.id),
    )
  }

  function addText() {
    add(splitEntries(text).map((entry) => build(byKey.get(catalogNameKey(entry.name)) ?? null, entry)))
    setText('')
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    addText()
  }

  const needle = searchKey(text)
  const suggestions =
    needle.length >= 2 && !text.includes(',')
      ? known.filter((ci) => !onTrip.has(ci.id) && searchKey(ci.name).includes(needle)).slice(0, MAX_SUGGESTIONS)
      : []
  const typedKnown = byKey.has(catalogNameKey(text.trim()))
  const templateOf = (ci: CatalogItem) => templates.find((t) => t.items.some((i) => i.catalogItemId === ci.id))
  const chips = frequent.filter((name) => !onTrip.has(`jmeno:${catalogNameKey(name)}`) && !onTrip.has(byKey.get(catalogNameKey(name))?.id ?? '')).slice(0, 6)

  return (
    <Card gap="sm" className="quick-add add-bar">
      <form className="add-row" onSubmit={submit}>
        <TextField
          aria-label="Co koupit"
          placeholder={primary ? 'Přidat, co chybí' : 'Přidat, třeba „2 kg rýže“'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => handleListPaste(e, setText)}
          className="grow"
          enterKeyHint="done"
          maxLength={500}
          autoComplete="off"
        />
        <Button kind={primary ? 'primary' : undefined} icon="plus" type="submit" aria-label="Přidat" className="square-btn" />
      </form>
      {suggestions.length > 0 && (
        <div className="add-suggest" role="listbox" aria-label="Známé zboží">
          {suggestions.map((ci) => {
            const template = templateOf(ci)
            const store = route.find((s) => s.id === ci.storeId)
            return (
              <button
                key={ci.id}
                type="button"
                role="option"
                aria-selected={false}
                className="add-suggest-row"
                onClick={() => {
                  add([build(ci, null)])
                  setText('')
                }}
              >
                <span className="text-col">
                  <span className="text-col-title">{ci.name}</span>
                  <span className="text-col-sub">
                    {[template ? `z šablony ${template.name}` : 'známé zboží', store?.name, formatQty(ci.defaultQty, ci.unit)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <Icon name="plus" size={20} className="tone-accent" />
              </button>
            )
          })}
          {!typedKnown && (
            <button type="button" className="add-suggest-row" onClick={addText}>
              <span className="text-col">
                <span className="text-col-title">{`Přidat „${text.trim()}“ jako novou věc`}</span>
              </span>
              <Icon name="plus" size={20} className="tone-accent" />
            </button>
          )}
        </div>
      )}
      <ScrollRow className="chips-scroll">
        {onBackToPlanning && (
          <Chip onClick={onBackToPlanning}>
            <Icon name="pencil" size={16} />
            Zpět k plánování
          </Chip>
        )}
        <Chip onClick={onPickTemplate}>
          <Icon name="list_check" size={16} />
          Ze šablony
        </Chip>
        {chips.map((name) => (
          <Chip key={name} plus onClick={() => add([build(byKey.get(catalogNameKey(name)) ?? null, { name, qty: 1, unit: null })])}>
            {name}
          </Chip>
        ))}
      </ScrollRow>
      {text === '' && !primary && <span className="meta muted">Víc věcí najednou odděl čárkou, množství napiš dopředu.</span>}
    </Card>
  )
}
